<template>
  <div class="textinput_wrapper pt-4">
    <div class="askform_container container">
      <h1>Ask CNS<span style="color: #549E93;">+</span></h1>
      <form @submit.prevent="sendRequest">
        <div class="row">
          <div class="col-md-11">
            <div class="form-group askform_text mb3">
              <label for="request"></label>
              <textarea @keydown="handleKeydown" ref="requestInputElement" class="form-control" v-model="requestText"
                id="request" required></textarea>
            </div>
          </div>
          <div class="col-md-1">
            <div class="form-group d-grid askform_button mb3">
              <button class="btn btn-primary btn-block" type="submit">Ask</button>
            </div>
          </div>
        </div>
      </form>
    </div>
    <div v-if="isLoading" class="d-flex justify-content-center pb-3">
      <div class="spinner-border" role="status"></div>
    </div>
    <!-- Content to display after loading -->
    <div v-else>
      <div ref="responseWrapper" class="response_wrapper">
        <div class="container pt-3">
          <div v-if="response">
            <h2>Response</h2>
            <VueMarkdown :markdown="response"> </VueMarkdown>
          </div>
          <div v-for="image in images" :key="image.url" class="mb-3">
            <img :src="image.url" alt="Image" class="img-fluid mb-3">
          </div>
          <div v-if="sources">
            <h2>
              Sources
            </h2>
            <div>
              <ul>
                <li v-for="(source, index) in sources" :key="index">
                  {{ source.name }}
                </li>
              </ul>
            </div>
            <div class="feedback">
              <h6>How was the response from CNS+?</h6>
              <i @click="sendFeedback('positive')" :class="{ 'feedbackGiven': feedbackGivenUp }"
                data-bs-toggle="tooltip" data-bs-placement="top" title="Good Response" class="fas fa-thumbs-up"
                style="cursor:pointer;"></i>
              <i @click="showFeedbackFormAndSendFeedback" :class="{ 'feedbackGiven': feedbackGivenDown }"
                data-bs-toggle="tooltip" data-bs-placement="top" title="Bad Response" class="fas fa-thumbs-down"
                style="cursor:pointer; margin-left: 1rem;"></i>
            </div>
            <div v-if="showFeedbackForm" class="feedback-form">
              <textarea v-model="feedbackText" class="form-control" placeholder="Enter your feedback"></textarea>
              <button @click="sendFeedback('negative')" class="btn btn-primary mt-2">Submit Feedback</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { VueMarkdown } from '@crazydos/vue-markdown'
import { ref, nextTick, watch } from "vue";
import { useAuthStore } from '@/store';
import { useThreadStore } from "@/store/modules/thread";
import { getSignedUrl } from '@aws-sdk/s3-request-presigner';
import { GetObjectCommand, S3Client } from '@aws-sdk/client-s3';
const images = ref([]);
const threadStore = useThreadStore();
const requestText = ref('');
const response = ref('');
const isLoading = ref(false);
const sources = ref('');
const sourcesRaw = ref('');
const showFeedbackForm = ref(false);
const feedbackText = ref('');
const feedbackGivenUp = ref(false);
const feedbackGivenDown = ref(false);
const responseWrapper = ref(null);
const requestInputElement = ref(null);

const sendRequest = async () => {
  try {
    isLoading.value = true;
    response.value = '';
    sources.value = '';
    images.value = [];
    sourcesRaw.value = '';
    showFeedbackForm.value = false;
    feedbackText.value = '';
    feedbackGivenUp.value = false;
    feedbackGivenDown.value = false;

    const user = useAuthStore();

    // Create the WebSocket connection
    const ws = new WebSocket(`wss://zyq3wbda1c.execute-api.us-east-1.amazonaws.com/prod?token=${user.token}`, [
      // optional subprotocols
    ]);

    ws.onopen = () => {
      ws.send(JSON.stringify({
        topic: requestText.value,
        user_id: user.user,
        session_id: threadStore.threadId ?? '',
        stream: true,
        action: "chat",
      }));
    };

    ws.onmessage = async (event) => {
      try {
        const jsonChunk = JSON.parse(event.data);

        if (jsonChunk.type == 'streaming_chunk') {
if (isLoading.value) {
            isLoading.value = false;
          }
          response.value += jsonChunk.data.text || '';
        }


        if (jsonChunk.type == 'stream_complete') {
          // Handle images
          if (jsonChunk.data?.images) {
            for (const image of jsonChunk.data.images) {
              if (image.url) {
                const presignedUrl = await gerenatePresignedUrl(image.url);
                images.value.push({ url: presignedUrl });
              }
            }
          }
          // Handle sources
          if (jsonChunk.data?.sources) {
            sources.value = jsonChunk.data.sources;
          }
        }



        // Update session
        if (jsonChunk.session_id) {
          threadStore.setThreadId(jsonChunk.session_id);
        }


        scrollToBottom();
        await nextTick();
        showFeedbackForm.value = true;
      } catch (e) {
        console.warn("Non-JSON message or parse error", event.data);
      }
    };

    ws.onerror = (err) => {
      console.error("WebSocket error", err);
      response.value = 'A connection error occurred.';
      isLoading.value = false;
      user.logout();
    };

    ws.onclose = () => {
      console.log("WebSocket closed");
      isLoading.value = false;
    };

  } catch (error) {
    response.value = 'An error occurred: ' + error.message;
    sources.value = '';
    isLoading.value = false;
  }
};


const handleKeydown = async (event) => {
  if (event.key === 'Enter') {
    if (event.shiftKey) return;
    event.preventDefault();
    await sendRequest();
  }
};

const handleThreadChange = (newThreadId, oldThreadId) => {
  if (newThreadId == null && oldThreadId != null) {
    requestText.value = '';
    clearAllResponseContent();
    requestInputElement.value?.focus();
  }
};

watch(() => threadStore.threadId, handleThreadChange);


const sendFeedback = async (type) => {
  try {
    const user = useAuthStore();
    const feedbackData = {
      question: requestText.value,
      response: response.value,
      feedback: type === 'positive' ? 'positive' : 'negative',
      email: user.user,
      feedback_text: feedbackText.value.trim(),
    };

    const res = await fetch('https://hrlabpnju5.execute-api.us-east-1.amazonaws.com/prod/feedback', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': user.token
      },
      body: JSON.stringify(feedbackData)
    });

    if (!res.ok) {
      throw new Error(`HTTP error! Status: ${res.status}`);
    }

    if (type === 'positive') {
      showFeedbackForm.value = false;
      feedbackText.value = '';
      feedbackGivenDown.value = false;
      feedbackGivenUp.value = true;
    } else {
      feedbackGivenDown.value = true;
      feedbackGivenUp.value = false;
      if (feedbackText.value.trim().length > 0) {
        showFeedbackForm.value = false;
        feedbackText.value = '';
      }
    }

  } catch (error) {
    console.log('An error occurred while submitting feedback: ' + error.message);
  }
};

const showFeedbackFormAndSendFeedback = () => {
  showFeedbackForm.value = true;
  sendFeedback('negative');
  nextTick(() => scrollToBottom());
};

const scrollToBottom = () => {
  const wrapper = responseWrapper.value;
  if (wrapper) {
    wrapper.scrollTop = wrapper.scrollHeight;
  }
};

const clearAllResponseContent = () => {
  const wrapper = responseWrapper.value;
  if (wrapper) {
    wrapper.innerHTML = '';
  }
};

const gerenatePresignedUrl = async (s3url) => {
  const s3 = new S3Client({
    region: 'us-east-1',
    credentials: {
      accessKeyId: 'AKIA3GPURJ7GDWEZ4SGK',
      secretAccessKey: 'wpqlQrItjWFKj4g5oOJ4wB2exHM0UzRCLjpfRvec',
    },
  });
  const s3urlWOPrefix = s3url.replace(/^s3:\/\//, "");
  const [bucket, ...keyParts] = s3urlWOPrefix.split("/");
  const objectKey = keyParts.join("/");

  const params = {
    Bucket: bucket,
    Key: objectKey,
  };
  let command = new GetObjectCommand(params)

  const url = await getSignedUrl(s3, command, { expiresIn: 30 });
  return url;
}
</script>


<style scoped>
.textinput_wrapper {
  padding-top: 1rem;
}

.askform_container {
  margin: auto;
}

.askform_text {
  margin-bottom: 1rem;
}

.askform_button {
  margin-bottom: 1rem;
}

.spinner-border {
  width: 3rem;
  height: 3rem;
}

.response_wrapper {
  padding-top: 1rem;
  overflow-y: auto;
  /* Enable vertical scrolling */
}

.container {
  max-width: 800px;
}

.relevance {
  display: none;
}

.relevance.visible {
  display: block;
}

.feedback {
  margin-top: 1rem;
}

.feedback-form {
  margin-top: 1rem;
}

.feedbackGiven {
  background-color: #c8d2ea;
  /* Add your desired background color */
}
</style>
